import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, Avatar, Chip
} from '@mui/material';
import { Add, Edit, Delete, People, AttachFile, Apartment as ApartmentIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { getApartments, createApartment, updateApartment, deleteApartment, USE_MOCK_DATA } from '../services/api';
import { mockApartments } from '../services/mockData';

export default function Apartments() {
  const [apartments, setApartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', totalFlats: '', openingFundBalance: '' });
  const [logoImage, setLogoImage] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const { isAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();

  const loadData = () => {
    if (USE_MOCK_DATA) {
      const userApartments = [mockApartments[0]];
      setApartments(userApartments);
      return;
    }
    getApartments().then(res => setApartments(res.data)).catch(() => {});
  };

  useEffect(() => { loadData(); }, []);

  const handleSubmit = async () => {
    const data = { name: form.name, address: form.address, totalFlats: parseInt(form.totalFlats), openingFundBalance: parseFloat(form.openingFundBalance) || 0, logoImage };
    if (USE_MOCK_DATA) {
      if (editing) {
        setApartments(prev => prev.map(a => a.id === editing.id ? { ...a, name: data.name, address: data.address, totalFlats: data.totalFlats, logoImageUrl: logoPreview || a.logoImageUrl } : a));
      } else {
        setApartments(prev => [...prev, { id: Date.now(), ...data, logoImageUrl: logoPreview, createdAt: new Date().toISOString(), isActive: true }]);
      }
    } else {
      try {
        if (editing) {
          await updateApartment(editing.id, data);
        } else {
          await createApartment(data);
        }
        loadData();
      } catch (err) {
        alert(err.response?.data || `Failed to ${editing ? 'update' : 'create'} apartment: ${err.message}`);
        return;
      }
    }
    setOpen(false);
    setEditing(null);
    setForm({ name: '', address: '', totalFlats: '' });
    setLogoImage(null);
    setLogoPreview(null);
  };

  const handleEdit = (apt) => {
    setEditing(apt);
    setForm({ name: apt.name, address: apt.address, totalFlats: apt.totalFlats.toString(), openingFundBalance: (apt.openingFundBalance || 0).toString() });
    setLogoPreview(apt.logoImageUrl || null);
    setLogoImage(null);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      if (USE_MOCK_DATA) {
        setApartments(prev => prev.filter(a => a.id !== id));
      } else {
        await deleteApartment(id);
        loadData();
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      setLogoImage(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleDialogClose = () => {
    setOpen(false);
    setEditing(null);
    setForm({ name: '', address: '', totalFlats: '', openingFundBalance: '' });
    setLogoImage(null);
    setLogoPreview(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Apartments</Typography>
        {isSuperAdmin() && (
          <Button variant="contained" startIcon={<Add />} onClick={() => { handleDialogClose(); setOpen(true); }}>
            Add Apartment
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Logo</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Address</TableCell>
              <TableCell>Total Flats</TableCell>
              <TableCell align="right">Opening Fund Balance</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {apartments.map(apt => (
              <TableRow key={apt.id}>
                <TableCell>
                  <Avatar
                    src={apt.logoImageUrl}
                    variant="rounded"
                    sx={{ width: 40, height: 40, bgcolor: '#e3f2fd' }}
                  >
                    <ApartmentIcon color="primary" />
                  </Avatar>
                </TableCell>
                <TableCell><strong>{apt.name}</strong></TableCell>
                <TableCell>{apt.address}</TableCell>
                <TableCell>{apt.totalFlats}</TableCell>
                <TableCell align="right">₹{(apt.openingFundBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                <TableCell>{new Date(apt.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  <IconButton onClick={() => navigate(`/apartments/${apt.id}/users`)} title="Manage Users">
                    <People />
                  </IconButton>
                  {isAdmin() && (
                    <>
                      <IconButton onClick={() => handleEdit(apt)}><Edit /></IconButton>
                      {isSuperAdmin() && (
                        <IconButton onClick={() => handleDelete(apt.id)} color="error"><Delete /></IconButton>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Apartment' : 'Add Apartment'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Name" margin="normal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <TextField fullWidth label="Address" margin="normal" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          <TextField fullWidth label="Total Flats" type="number" margin="normal" value={form.totalFlats} onChange={(e) => setForm({ ...form, totalFlats: e.target.value })} />
          <TextField fullWidth label="Opening Fund Balance (₹)" type="number" margin="normal" value={form.openingFundBalance} onChange={(e) => setForm({ ...form, openingFundBalance: e.target.value })}
            helperText="Total fund the apartment had before using this app" />

          {/* Logo Upload */}
          <Box sx={{ mt: 2, p: 2, border: '1px dashed #ccc', borderRadius: 1, textAlign: 'center' }}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".jpg,.jpeg,.png,.webp,.svg"
              style={{ display: 'none' }}
            />
            {logoPreview && (
              <Box sx={{ mb: 1 }}>
                <Avatar
                  src={logoPreview}
                  variant="rounded"
                  sx={{ width: 80, height: 80, mx: 'auto', mb: 1 }}
                />
              </Box>
            )}
            <Button
              variant="outlined"
              startIcon={<AttachFile />}
              onClick={() => fileInputRef.current?.click()}
              size="small"
            >
              {logoPreview ? 'Change Logo' : 'Upload Logo (Optional)'}
            </Button>
            {logoImage && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  label={logoImage.name}
                  onDelete={() => { setLogoImage(null); setLogoPreview(editing?.logoImageUrl || null); }}
                  size="small"
                  color="success"
                />
              </Box>
            )}
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              Accepted: JPG, PNG, WebP, SVG (max 2MB)
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">{editing ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
